/**
 * NewNegotiationDialog — Créer une négociation depuis une opération.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = { headers: { Authorization: 'Bearer cookie-auth' } };

export const NewNegotiationDialog = ({ open, onClose, onCreated, types = [] }) => {
  const [ops, setOps] = useState([]);
  const [operationId, setOperationId] = useState('');
  const [type, setType] = useState('price_change');
  const [message, setMessage] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/transport-requests?limit=100000`, AUTH);
        const list = Array.isArray(data) ? data : (data.items || data.operations || data.transport_requests || []);
        setOps(list.filter((o) => o.id).slice(0, 100));
      } catch (e) { setOps([]); }
    })();
    setOperationId(''); setType('price_change'); setMessage(''); setProposedPrice('');
  }, [open]);

  const submit = async () => {
    if (!operationId) { toast.error('Sélectionnez une opération'); return; }
    if (!message.trim()) { toast.error('Saisissez un message initial'); return; }
    setBusy(true);
    try {
      const op = ops.find((o) => o.id === operationId);
      const prev = op?.finalPrice ?? op?.estimatedPrice ?? null;
      const offerPayload = proposedPrice && !isNaN(Number(proposedPrice))
        ? { proposedPrice: Number(proposedPrice), previousPrice: prev } : undefined;
      await axios.post(`${API}/negotiations`, {
        operationId, negotiationType: type, initialMessage: message, offerPayload,
      }, AUTH);
      toast.success('Négociation créée');
      onCreated?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la création'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="new-nego-dialog">
        <DialogHeader><DialogTitle>Nouvelle négociation</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Opération</label>
            <Select value={operationId} onValueChange={setOperationId}>
              <SelectTrigger data-testid="new-nego-operation"><SelectValue placeholder={ops.length ? 'Choisir une opération' : 'Aucune opération disponible'} /></SelectTrigger>
              <SelectContent>
                {ops.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.operationNumber || o.id} · {o.companyName || o.clientName || 'Client'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="new-nego-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Prix proposé (optionnel)</label>
            <Input type="number" value={proposedPrice} onChange={(e) => setProposedPrice(e.target.value)} placeholder="ex : 680000" data-testid="new-nego-price" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Message initial</label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Décrivez la demande de négociation…" data-testid="new-nego-message" className="resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={submit} disabled={busy} data-testid="new-nego-submit">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewNegotiationDialog;
